import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, X } from "lucide-react";

/**
 * Banner shown when an updated service worker has been installed
 * and is waiting to take control. Only renders if a service worker
 * already exists (we never register one ourselves).
 *
 * Tap "Aktualisieren" → posts SKIP_WAITING and reloads.
 */
export default function ServiceWorkerUpdateBanner() {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

    let cancelled = false;

    navigator.serviceWorker.getRegistration().then((reg) => {
      if (cancelled || !reg) return;

      // Already-waiting worker
      if (reg.waiting) setWaiting(reg.waiting);

      // Listen for new updates
      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            setWaiting(newWorker);
          }
        });
      });
    });

    // When the new SW takes control, reload
    const onControllerChange = () => {
      if (!cancelled) window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  if (!waiting || dismissed) return null;

  const apply = () => {
    try {
      waiting.postMessage({ type: "SKIP_WAITING" });
    } catch {
      window.location.reload();
    }
  };

  return (
    <div
      role="status"
      className="fixed bottom-20 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground shadow-xl border border-primary/40 animate-fade-in max-w-[92vw]"
    >
      <RefreshCw className="w-4 h-4 shrink-0" />
      <span className="text-sm font-semibold">Update verfügbar</span>
      <Button
        size="sm"
        variant="secondary"
        onClick={apply}
        className="h-7 px-3 ml-1"
      >
        Neu laden
      </Button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Schließen"
        className="p-1 rounded-full hover:bg-primary-foreground/10 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
